import React, { useEffect, useState } from "react";
import "./PaymentReport.css";
import DetailedPaymentReport from "./DetailedPaymentReport";
import axios from "axios";
import { API_BASE_URL } from "../../../../Constants/ApiEndpoints";
import { FaFileInvoiceDollar, FaWallet, FaMobileAlt, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import DateRangePickerComponent from "../../../../CommonComponents/DateRangePickerComponent";

const PaymentReport = () => {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const completedPayments =
    reportData?.detailedPaymentReportTableResponse?.completedPayments || [];
  const pendingPayments =
    reportData?.detailedPaymentReportTableResponse?.pendingPayments || [];

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const fetchReportData = async (sDate = startDate, eDate = endDate) => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.get(`${API_BASE_URL}/getPaymentReport`, {
        params: {
          fromDate: formatDate(sDate),
          toDate: formatDate(eDate)
        }
      });
      setReportData(response.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch payment report data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData(startDate, endDate);
  }, [startDate, endDate]);

  const handleDateChange = (s, e) => {
    setStartDate(s);
    setEndDate(e);
  };

  const totalCollected = reportData?.totalAmountCollected || 0;
  const upiCollected = reportData?.amountCollectedInUpi || 0;
  const rawCash = reportData?.amountCollectedInCash || 0;

  // Ensure cash collected does not exceed total realized revenue
  const cashCollected = (rawCash > totalCollected)
    ? Math.max(0, totalCollected - upiCollected)
    : rawCash;

  return (
    <div className="payment-report-wrapper">
      <div className="unified-master-card">
        {/* === Master Card Header === */}
        <div className="report-master-header">
          <div className="header-title-group">
            <div className="header-icon-box">
              <FaFileInvoiceDollar size={18} color="#2563eb" />
            </div>
            <div>
              <div className="header-subtitle">OUTPATIENT REPORTS</div>
              <h2 className="header-title">Payment Collection Analytics</h2>
            </div>
          </div>

          <div className="header-actions-group">
            <DateRangePickerComponent
              onDateChange={handleDateChange}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex-center-card" style={{ minHeight: '300px' }}>
            <div className="loading-spinner-ring"></div>
            <p className="loading-text">Loading Payment Collection Analytics...</p>
          </div>
        ) : error ? (
          <div className="flex-center-card error-card" style={{ minHeight: '300px' }}>
            <FaExclamationTriangle size={32} color="#e11d48" />
            <p className="error-text">{error}</p>
          </div>
        ) : reportData ? (
          <>
            {/* === KPI Metrics Grid === */}
            <div className="pay-kpi-metrics-grid">
              {/* 1. Total Invoiced */}
              <div className="pay-kpi-card pay-invoiced-kpi">
                <div className="pay-kpi-header">
                  <span className="pay-kpi-title">Total Invoiced</span>
                  <div className="pay-kpi-icon-badge pay-invoiced-icon">
                    <FaFileInvoiceDollar size={14} />
                  </div>
                </div>
                <div className="pay-kpi-amount">
                  ₹ {(reportData.totalInvoicedAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="pay-kpi-subtext">Gross billed amount</div>
              </div>

              {/* 2. Total Collected */}
              <div className="pay-kpi-card pay-total-kpi">
                <div className="pay-kpi-header">
                  <span className="pay-kpi-title">Total Collected</span>
                  <div className="pay-kpi-icon-badge pay-total-icon">
                    <FaCheckCircle size={14} />
                  </div>
                </div>
                <div className="pay-kpi-amount pay-total-text">
                  ₹ {totalCollected.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="pay-kpi-subtext">Net realized revenue</div>
              </div>

              {/* 3. Collected in Cash */}
              <div className="pay-kpi-card pay-cash-kpi">
                <div className="pay-kpi-header">
                  <span className="pay-kpi-title">Collected in Cash</span>
                  <div className="pay-kpi-icon-badge pay-cash-icon">
                    <FaWallet size={14} />
                  </div>
                </div>
                <div className="pay-kpi-amount">
                  ₹ {cashCollected.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="pay-kpi-subtext">Direct cash payments</div>
              </div>

              {/* 4. Collected via UPI */}
              <div className="pay-kpi-card pay-upi-kpi">
                <div className="pay-kpi-header">
                  <span className="pay-kpi-title">Collected via UPI</span>
                  <div className="pay-kpi-icon-badge pay-upi-icon">
                    <FaMobileAlt size={14} />
                  </div>
                </div>
                <div className="pay-kpi-amount">
                  ₹ {upiCollected.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="pay-kpi-subtext">Digital & QR transfers</div>
              </div>

              {/* 5. Total Pending */}
              <div className="pay-kpi-card pay-pending-kpi">
                <div className="pay-kpi-header">
                  <span className="pay-kpi-title">Total Pending</span>
                  <div className="pay-kpi-icon-badge pay-pending-icon">
                    <FaExclamationTriangle size={14} />
                  </div>
                </div>
                <div className="pay-kpi-amount pay-pending-text">
                  ₹ {(reportData.totalPendingAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="pay-kpi-subtext">Outstanding dues</div>
              </div>
            </div>

            {/* === Detailed Tables Section === */}
            <div className="reports-tables-section">
              {/* Completed Payments Table */}
              <DetailedPaymentReport
                data={completedPayments}
                title="Completed Payments"
                type="completed"
                fromDate={formatDate(startDate)}
                toDate={formatDate(endDate)}
              />

              {/* Pending Payments Table */}
              <DetailedPaymentReport
                data={pendingPayments}
                title="Pending Payments"
                type="pending"
                fromDate={formatDate(startDate)}
                toDate={formatDate(endDate)}
              />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default PaymentReport;
